import React, { useCallback, useEffect, useState } from 'react'
import Cookie from 'cookie-universal'
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
} from '@tanstack/react-query'
import { ApiError, CartsService } from '@mc/api'
import { BogoBanner } from '@mc/bogo'
import { DISCOUNT_COOKIE_KEY } from '@mc/constants'
import { IconNotificationErrorDefault, Toast } from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { trackError } from '@mc/monitoring'
import { paths } from '@mc/paths'
import { Cta, PlansProvider, usePlansContext } from '@mc/plans-ui'
import { trackInitiateCheckout } from '@mc/pre-paywall-events'
import { PromoBanner } from '@mc/promos'
import { useStaticData } from '@mc/static-data-react'
import { Matrix, Navigation, PlansHeader } from '../../components'
import {
  IntroPricingBanner,
  useIntroPricingExperimentViewed,
} from '../../experiments/prcng-338_v1_intro_pricing'
import { useTrackPlanZFreeGiftExperimentViewed } from '../../experiments/prcng-955_v1_plan_z_bogo'
import {
  addCertsQuizCart,
  CERTS_PARAM,
  CERTS_QUIZ_PLANS_I18N_NAMESPACE,
  CERTS_VALUES,
  CertificatesAddOnCard,
  CertsAddOnProvider,
  useCertsAddOn,
} from '../../experiments/prcng-1138_v1_certs_quiz_plans'
import { useMCProExperiment } from '../../experiments/prcng-1194_v1_mc_pro_tier'
import { useMCProPromoExperiment } from '../../experiments/prcng-1270_v1_mc_pro_promo'
import { CHECKOUT_I18N_NAMESPACE } from '../../i18n'
import { trackStickyFooterViewed } from './events'
import classes from './PlansSelectionPage.module.scss'

const client = new QueryClient()
const cookies = Cookie()

export const PlansSelectionPage = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const { isGift } = useStaticData<{ isGift: boolean }>()

  useIntroPricingExperimentViewed()
  useTrackPlanZFreeGiftExperimentViewed()
  useMCProExperiment({ trackExposure: true })
  useMCProPromoExperiment({ trackExposure: true })

  return (
    <PlansProvider>
      <Navigation />
      <PromoBanner pathname={paths.checkout} isGift={isGift} />
      <BogoBanner location='checkout' isGift={isGift} />
      <IntroPricingBanner />
      <Container>
        <PlansHeader />
        <Matrix />
        <QueryClientProvider client={client}>
          <CertsAddOnProvider>
            <CertificatesAddOnCard />
            <CheckoutCta />
          </CertsAddOnProvider>
        </QueryClientProvider>
      </Container>
      {children}
    </PlansProvider>
  )
}

const Container = ({ children }: { children: React.ReactNode }) => (
  <div className='mc-py-8 d-block'>
    <div className={classes.ctaContainer}>{children}</div>
  </div>
)

const useCtaCopy = () => {
  const { t } = useTranslation(CHECKOUT_I18N_NAMESPACE)
  const { t: tCerts } = useTranslation(CERTS_QUIZ_PLANS_I18N_NAMESPACE)
  const { selectedPlan } = usePlansContext()
  const { isAdded: isCertsAddOnAdded, isCardVisible: isCertsAddOnCardVisible } =
    useCertsAddOn()
  const { isVariant2: isMCProVariant2 } = useMCProExperiment()
  const plan = selectedPlan?.displayName
  const planCta = t('plans.cta.label', { plan })

  if (isCertsAddOnAdded) {
    return {
      text: tCerts('certsAddOn.cta', { plan }),
      ariaLabel: tCerts('certsAddOn.ctaLabel', { plan }),
    }
  }

  if (isCertsAddOnCardVisible) return { text: planCta, ariaLabel: planCta }

  // TODO(PRCNG-1232): confirm with design that "Continue with {plan}" is mobile-only and that mobile means below lg.
  if (isMCProVariant2) {
    return {
      text: (
        <>
          <span className='d-lg-none'>{planCta}</span>
          <span className='d-none d-lg-inline'>{t('plans.cta.text')}</span>
        </>
      ),
      ariaLabel: planCta,
    }
  }

  return { text: t('plans.cta.text'), ariaLabel: planCta }
}

const CheckoutCta = () => {
  const { selectedPlan } = usePlansContext()
  const ctaCopy = useCtaCopy()
  // prcng-1138_v1 — `productId` is 0 until the add-on offer resolves, and
  // adding product 0 would 404.
  const {
    isAdded: isCertsAddOnAdded,
    isCampaign: isCertsAddOnCampaign,
    productId: certsAddOnProductId,
    isLoading: isCertsAddOnLoading,
  } = useCertsAddOn()
  const shouldAddCertificate = isCertsAddOnAdded && !!certsAddOnProductId
  // Only a visitor holding a selection has anything to wait for. Widening this
  // to `isCertsAddOnLoading` alone would put the offer request on the critical
  // path of the whole treatment arm, delaying Continue for visitors the
  // certificate never touches — on the very population the experiment measures.
  const isHoldingForCertificate = isCertsAddOnAdded && isCertsAddOnLoading
  const selectedProductId = selectedPlan?.productId
  const { isGift } = useStaticData<{
    isGift: boolean
  }>()

  const addedValue = isCertsAddOnCampaign
    ? CERTS_VALUES.addedFromCampaign
    : CERTS_VALUES.added
  const declinedValue = isCertsAddOnCampaign
    ? CERTS_VALUES.declinedFromCampaign
    : CERTS_VALUES.declined

  const addProductToCart = useMutation({
    mutationFn: async ({ search }: { search: string }) => {
      if (typeof selectedProductId !== 'number') {
        throw new Error('No selected product ID available')
      }

      // If we have gift=true query param, we need to pass it to the checkout payment flow.
      const queryParams = new URLSearchParams(search)

      // prcng-1138_v1 — branched rather than folded in, so the path without the
      // add-on keeps exactly one request and the same discount slug.
      if (shouldAddCertificate) {
        const { hasCertificate } = await addCertsQuizCart({
          productId: selectedProductId,
          certificateProductId: certsAddOnProductId,
        })

        // Only a confirmed line says `added`: the payment step acts on this, so
        // a failed add must not leave it trying to restore a certificate the
        // backend refused. A campaign visitor gets the off-bucket spellings,
        // which is what lets the payment step keep its bucket check for
        // everyone else, and keeps the tier collection it validates against
        // the one this page showed (PRCNG-1193).
        queryParams.set(
          CERTS_PARAM,
          hasCertificate ? addedValue : declinedValue,
        )
      } else {
        await CartsService.addProductToCart({
          requestBody: {
            productId: selectedProductId,
            gift: isGift,
            productType: isGift ? 'gift' : 'subscription',
            discountSlug: cookies.get(DISCOUNT_COOKIE_KEY) || undefined,
          },
        })

        // Continue is the decision point, so `quiz` never survives it. Only
        // rewritten for visitors already carrying the param — anyone outside
        // the funnel keeps a URL this experiment has not touched.
        if (queryParams.has(CERTS_PARAM)) {
          queryParams.set(CERTS_PARAM, declinedValue)
        }
      }

      window.location.href = paths.checkoutPayment(
        selectedProductId,
        queryParams.toString(),
      )
    },
    onError: (error) => {
      trackError(error as ApiError, {
        productId: selectedProductId?.toString() ?? '',
        isGift: isGift.toString(),
        discountSlug: cookies.get(DISCOUNT_COOKIE_KEY) ?? '',
      })
    },
  })

  const onClickCta = useCallback(() => {
    // `Cta` takes `loading` but no `disabled`, so a second click still lands. A
    // concurrent run's subscription add would evict the first run's certificate
    // after its read-back had already passed. Held for a pending selection too,
    // or a card selected before `productId` lands reads as a decline.
    if (addProductToCart.isLoading || isHoldingForCertificate) return

    trackInitiateCheckout({ product: selectedPlan?.product, isGift })
    addProductToCart.mutate({ search: window.location.search })
  }, [addProductToCart, isHoldingForCertificate, selectedPlan, isGift])

  return (
    <>
      <Cta
        onClick={onClickCta}
        ariaLabel={ctaCopy.ariaLabel}
        loading={
          isHoldingForCertificate ||
          addProductToCart.isLoading ||
          addProductToCart.isSuccess /* if success, JS redirect to next page is pending. Show loader */
        }
        text={ctaCopy.text}
        sticky={{
          alwaysVisible: false,
          containerClassName: `mc-p-2 ${classes.ctaContainer}`,
          onFirstVisible: trackStickyFooterViewed,
        }}
      />
      <ErrorToast show={!!addProductToCart.isError} />
    </>
  )
}

const ErrorToast = ({ show }: { show: boolean }) => {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) return null

  return (
    <Toast show={show} kind='error'>
      <div className='d-flex align-items-center'>
        <IconNotificationErrorDefault className='mc-mr-2' />
        <div>
          <Trans i18nKey='common.error.addToCart' ns='@mc/checkout'>
            <a
              className='mc-text--link'
              target='_blank'
              rel='noopener noreferrer'
              href={paths.helpCenter}
            />
          </Trans>
        </div>
      </div>
    </Toast>
  )
}
